import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE = '/api';

const CURRENCY_SYMBOL: Record<string, string> = { ILS: '₪', USD: '$', EUR: '€', GBP: '£' };

interface RequestInfo {
  id: string;
  orgId: string;
  orgName: string;
  orgLogo?: string;
  terminalId: string;
  campaignId?: string;
  contactId?: string;
  title: string;
  notes?: string;
  amount: number;
  currency: string;
  allowCustomAmount: boolean;
  payerName?: string;
  payerEmail?: string;
  payerPhone?: string;
  status: string;
}

export default function PaymentLinkPage() {
  const { token } = useParams<{ token: string }>();
  const [info, setInfo] = useState<RequestInfo | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const [amount, setAmount] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const [payUrl, setPayUrl] = useState('');
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/public/payment-request/${token}`);
        if (cancelled) return;
        setInfo(data);
        setAmount(String(data.amount));
        setName(data.payerName || '');
        setEmail(data.payerEmail || '');
        setPhone(data.payerPhone || '');
      } catch (err: any) {
        if (!cancelled) setError(err?.response?.data?.error || 'הקישור לא נמצא');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const startPayment = async () => {
    if (!info) return;
    const amt = info.allowCustomAmount ? Number(amount) : info.amount;
    if (!Number.isFinite(amt) || amt <= 0) { setError('סכום לא תקין'); return; }
    setStarting(true);
    setError('');
    try {
      const params = new URLSearchParams({
        amount: String(amt),
        orgId: info.orgId,
        terminalId: info.terminalId,
        paymentRequestId: info.id,
        currency: info.currency || 'ILS',
      });
      if (info.campaignId) params.set('campaignId', info.campaignId);
      if (info.contactId) params.set('contactId', info.contactId);
      if (name.trim()) params.set('clientName', name.trim());
      if (email.trim()) params.set('clientEmail', email.trim());
      if (phone.trim()) params.set('clientPhone', phone.trim());
      const { data } = await axios.get(`${API_BASE}/public/payment-url?${params.toString()}`);
      if (data?.url) setPayUrl(data.url);
      else setError('לא ניתן לפתוח את עמוד התשלום');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'שגיאה בפתיחת התשלום');
    } finally {
      setStarting(false);
    }
  };

  const sym = info ? (CURRENCY_SYMBOL[info.currency] || '') : '';

  return (
    <div dir="rtl" className="min-h-screen bg-gray-50 flex items-start justify-center p-4">
      <div className="w-full max-w-md mt-8 bg-white rounded-2xl shadow-md overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500">טוען…</div>
        ) : error && !info ? (
          <div className="p-10 text-center text-red-600">{error}</div>
        ) : info ? (
          <>
            <div className="p-6 text-center border-b border-gray-100">
              {info.orgLogo && <img src={info.orgLogo} alt={info.orgName} className="h-12 mx-auto mb-3 object-contain" />}
              <div className="text-sm text-gray-500">{info.orgName}</div>
              <h1 className="text-xl font-bold text-gray-900 mt-1">{info.title}</h1>
              {info.notes && <p className="text-sm text-gray-600 mt-2 whitespace-pre-line">{info.notes}</p>}
            </div>

            {info.status === 'paid' && !payUrl && (
              <div className="mx-6 mt-4 p-2 rounded-lg bg-emerald-50 text-emerald-700 text-sm text-center">
                תשלום זה כבר בוצע. ניתן לשלם שוב אם תרצה.
              </div>
            )}

            {payUrl ? (
              <iframe
                src={payUrl}
                title="תשלום מאובטח"
                className="w-full"
                style={{ height: 620, border: 0 }}
                allow="payment *"
              />
            ) : (
              <div className="p-6 space-y-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">סכום לתשלום</label>
                  <div className="relative">
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">{sym}</span>
                    <input
                      type="number"
                      min="0"
                      value={amount}
                      disabled={!info.allowCustomAmount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="w-full pr-8 pl-3 py-2.5 border border-gray-300 rounded-lg text-lg font-bold disabled:bg-gray-50 disabled:text-gray-700"
                    />
                  </div>
                </div>
                <input className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" placeholder="שם מלא" value={name} onChange={(e) => setName(e.target.value)} />
                <input className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" placeholder="אימייל (לקבלת קבלה)" value={email} onChange={(e) => setEmail(e.target.value)} />
                <input className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" placeholder="טלפון" value={phone} onChange={(e) => setPhone(e.target.value)} />

                {error && <div className="p-2 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}

                <button
                  onClick={startPayment}
                  disabled={starting}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold rounded-xl transition-colors"
                >
                  {starting ? 'פותח…' : 'המשך לתשלום'}
                </button>
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
